import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { supabase } from './supabaseClient';
import './AnalyticsDashboard.css'; // Keeping original CSS import
import './custom.css'; // Adding new CSS import

const AnalyticsDashboard = ({ user, handleLogout }) => {
  // State management combining both versions
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState({
    userActivity: [],
    challenges: [],
    retention: [],
    streaks: [],
    monetization: [],
    sharing: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: '2025-02-20',
    endDate: '2025-03-22'
  });
  const [useMockData, setUseMockData] = useState(true);

  // Chart colors
  const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
  const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

  // Mock challenge data from first version
  const mockChallengeData = [
    { id: 1, emoji: '🦄', name: 'Challenge 1: Do something weird and fun', completions: 278, uniqueUsers: 220, avgHour: '12:00' },
    { id: 2, emoji: '🎪', name: 'Challenge 2: Do something weird and fun', completions: 488, uniqueUsers: 235, avgHour: '13:00' },
    { id: 3, emoji: '🌮', name: 'Challenge 3: Do something weird and fun', completions: 419, uniqueUsers: 68, avgHour: '14:00' },
    { id: 4, emoji: '🌈', name: 'Challenge 4: Do something weird and fun', completions: 241, uniqueUsers: 143, avgHour: '15:00' },
    { id: 5, emoji: '🧁', name: 'Challenge 5: Do something weird and fun', completions: 349, uniqueUsers: 305, avgHour: '16:00' },
    { id: 6, emoji: '🦊', name: 'Challenge 6: Do something weird and fun', completions: 218, uniqueUsers: 335, avgHour: '17:00' },
    { id: 7, emoji: '🌝', name: 'Challenge 7: Do something weird and fun', completions: 408, uniqueUsers: 321, avgHour: '18:00' },
    { id: 8, emoji: '👻', name: 'Challenge 8: Do something weird and fun', completions: 299, uniqueUsers: 268, avgHour: '19:00' },
  ];

  // Get auth token helper function
  const getAuthToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  };

  // Combined fetch data function
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (useMockData) {
          // Use mock data from both versions
          const mockData = generateMockData();
          setData({
            ...mockData,
            challenges: mockChallengeData.map(ch => ({
              ...ch,
              task: ch.name,
              icon: ch.emoji,
              completion_count: ch.completions,
              unique_users: ch.uniqueUsers,
              avg_completion_hour: parseInt(ch.avgHour.split(':')[0])
            }))
          });
        } else {
          const { data: { session } } = await supabase.auth.getSession();
          
          if (!session && user) {
            await supabase.auth.signInWithPassword({
              email: 'your-test-account@example.com',
              password: 'your-test-password'
            });
          }
          
          const token = await getAuthToken();
          
          const [userActivityResponse, retentionResponse, streakResponse, challengeResponse, monetizationResponse, sharingResponse] = await Promise.all([
            fetch(`${SUPABASE_URL}/functions/v1/user-activity-metrics?start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`, 
              { headers: { Authorization: `Bearer ${token}` } }),
            fetch(`${SUPABASE_URL}/functions/v1/retention-metrics?start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`, 
              { headers: { Authorization: `Bearer ${token}` } }),
            fetch(`${SUPABASE_URL}/functions/v1/streak-distribution?start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`, 
              { headers: { Authorization: `Bearer ${token}` } }),
            fetch(`${SUPABASE_URL}/functions/v1/challenge-stats?start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`, 
              { headers: { Authorization: `Bearer ${token}` } }),
            fetch(`${SUPABASE_URL}/functions/v1/monetization-metrics?start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`, 
              { headers: { Authorization: `Bearer ${token}` } }),
            fetch(`${SUPABASE_URL}/functions/v1/sharing-metrics?start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`, 
              { headers: { Authorization: `Bearer ${token}` } })
          ]);

          const [userActivity, retention, streaks, challenges, monetization, sharing] = await Promise.all([
            userActivityResponse.json(),
            retentionResponse.json(),
            streakResponse.json(),
            challengeResponse.json(),
            monetizationResponse.json(),
            sharingResponse.json()
          ]);

          setData({
            userActivity: userActivity.data || [],
            retention: retention.data || [],
            streaks: streaks.data || [],
            challenges: challenges.data || [],
            monetization: monetization.data || [],
            sharing: sharing.data || []
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('An error occurred while fetching data. Please try again later.');
        if (useMockData) {
          const mockData = generateMockData();
          setData({
            ...mockData,
            challenges: mockChallengeData.map(ch => ({
              ...ch,
              task: ch.name,
              icon: ch.emoji,
              completion_count: ch.completions,
              unique_users: ch.uniqueUsers,
              avg_completion_hour: parseInt(ch.avgHour.split(':')[0])
            }))
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange, useMockData, user]);

  // Generate mock data from second version
  const generateMockData = () => {
    const userActivity = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - 29 + i);
      return {
        date: date.toISOString().split('T')[0],
        daily_active_users: Math.floor(Math.random() * 100) + 50,
        weekly_active_users: Math.floor(Math.random() * 300) + 150,
        monthly_active_users: Math.floor(Math.random() * 500) + 300
      };
    });

    const retention = Array.from({ length: 10 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - 90 + i * 7);
      return {
        cohort_date: date.toISOString().split('T')[0],
        cohort_size: Math.floor(Math.random() * 100) + 20,
        day_1_retention: Math.floor(Math.random() * 40) + 60,
        day_7_retention: Math.floor(Math.random() * 30) + 30,
        day_30_retention: Math.floor(Math.random() * 20) + 10
      };
    });

    const streaks = Array.from({ length: 10 }, (_, i) => ({
      streak_length: i + 1,
      user_count: Math.floor(Math.random() * 500) * (10 / (i + 1)),
      percentage: Math.floor(Math.random() * 30) * (10 / (i + 1))
    }));

    const monetization = {
      revenue: Array.from({ length: 30 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - 29 + i);
        return {
          date: date.toISOString().split('T')[0],
          daily_revenue: Math.floor(Math.random() * 100) + 10,
          premium_users: Math.floor(Math.random() * 50) + 5,
          conversion_rate: (Math.random() * 5 + 1).toFixed(2)
        };
      }),
      purchases: [
        { item_name: 'Premium Subscription', count: Math.floor(Math.random() * 100) + 50, revenue: Math.floor(Math.random() * 1000) + 500 },
        { item_name: 'Extra Challenge Pack', count: Math.floor(Math.random() * 100) + 30, revenue: Math.floor(Math.random() * 500) + 200 },
        { item_name: 'Streak Protector', count: Math.floor(Math.random() * 100) + 80, revenue: Math.floor(Math.random() * 300) + 100 },
        { item_name: 'Theme Pack', count: Math.floor(Math.random() * 100) + 20, revenue: Math.floor(Math.random() * 200) + 50 }
      ]
    };

    const sharing = {
      shares: Array.from({ length: 30 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - 29 + i);
        return {
          date: date.toISOString().split('T')[0],
          challenge_shares: Math.floor(Math.random() * 50) + 10,
          streak_shares: Math.floor(Math.random() * 30) + 5,
          achievement_shares: Math.floor(Math.random() * 20) + 5,
          total_shares: Math.floor(Math.random() * 100) + 20
        };
      }),
      platforms: [
        { platform: 'Instagram', count: Math.floor(Math.random() * 500) + 300 },
        { platform: 'Twitter/X', count: Math.floor(Math.random() * 300) + 200 },
        { platform: 'Facebook', count: Math.floor(Math.random() * 200) + 100 },
        { platform: 'TikTok', count: Math.floor(Math.random() * 400) + 250 },
        { platform: 'WhatsApp', count: Math.floor(Math.random() * 600) + 400 }
      ]
    };

    return { userActivity, retention, streaks, monetization, sharing };
  };

  const handleDateChange = (e) => {
    setDateRange({
      ...dateRange,
      [e.target.name]: e.target.value
    });
  };

  const applyDateFilter = () => {
    // Trigger data fetch with new date range
  };

  const retentionStats = useMemo(() => {
    if (!data.retention || data.retention.length === 0) return null;
    return {
      avg1DayRetention: (data.retention.reduce((sum, item) => sum + item.day_1_retention, 0) / data.retention.length).toFixed(1),
      avg7DayRetention: (data.retention.reduce((sum, item) => sum + item.day_7_retention, 0) / data.retention.length).toFixed(1),
      avg30DayRetention: (data.retention.reduce((sum, item) => sum + item.day_30_retention, 0) / data.retention.length).toFixed(1)
    };
  }, [data.retention]);

  const streakStats = useMemo(() => {
    if (!data.streaks || data.streaks.length === 0) return null;
    const totalUsers = data.streaks.reduce((sum, item) => sum + item.user_count, 0);
    const totalStreakDays = data.streaks.reduce((sum, item) => sum + (item.streak_length * item.user_count), 0);
    return {
      averageStreak: (totalStreakDays / totalUsers).toFixed(1),
      maxStreak: data.streaks.reduce((max, item) => Math.max(max, item.streak_length), 0),
      usersWithLongStreak: data.streaks.filter(item => item.streak_length >= 7).reduce((sum, item) => sum + item.user_count, 0)
    };
  }, [data.streaks]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-indigo-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600 mx-auto"></div>
          <p className="mt-6 text-lg text-indigo-600 font-medium">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard bg-gray-50 min-h-screen">
      <header className="bg-gradient-to-r from-indigo-700 to-indigo-500 p-4 text-white shadow-lg sticky top-0 z-10">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold">Daily Weird Win Admin Analytics</h1>
          <button
            onClick={handleLogout}
            className="logout-btn bg-white text-indigo-700 hover:bg-indigo-100 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150"
          >
            Log Out
          </button>
        </div>
      </header>

      <div className="container mx-auto p-4 analytics-content">
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="date-controls flex flex-col md:flex-row gap-4 items-center">
            <div className="date-field w-full md:w-auto">
              <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                id="start-date"
                type="date"
                name="startDate"
                value={dateRange.startDate}
                onChange={handleDateChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div className="date-field w-full md:w-auto">
              <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                id="end-date"
                type="date"
                name="endDate"
                value={dateRange.endDate}
                onChange={handleDateChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div className="w-full md:w-auto self-end">
              <button 
                onClick={applyDateFilter}
                className="update-btn w-full md:w-auto bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 transition duration-200"
              >
                Update
              </button>
            </div>
            <div className="mock-data-toggle w-full md:w-auto flex items-center ml-4">
              <label htmlFor="mock-data" className="flex items-center cursor-pointer">
                <div className="relative">
                  <input 
                    id="mock-data"
                    type="checkbox"
                    className="sr-only"
                    checked={useMockData}
                    onChange={() => setUseMockData(!useMockData)}
                  />
                  <div className={`block w-14 h-8 rounded-full ${useMockData ? 'bg-indigo-600' : 'bg-gray-300'}`}></div>
                  <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition ${useMockData ? 'transform translate-x-6' : ''}`}></div>
                </div>
                <div className="ml-3 text-sm font-medium text-gray-700">Use Mock Data</div>
              </label>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded shadow">
            {error}
          </div>
        )}

        <div className="tabs mb-6">
          <nav className="flex -mb-px border-b border-gray-200">
            {['overview', 'retention', 'challenges', 'streaks', 'monetization', 'sharing'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm transition duration-200 ${
                  activeTab === tab
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-lg font-semibold mb-4">User Activity (DAU/WAU/MAU)</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.userActivity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis width={50} />
                  <Tooltip
                    formatter={(value) => [value, 'Users']}
                    labelFormatter={(date) => new Date(date).toLocaleDateString('en-US')}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="daily_active_users" stroke={COLORS[0]} name="DAU" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="weekly_active_users" stroke={COLORS[1]} name="WAU" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="monthly_active_users" stroke={COLORS[2]} name="MAU" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-lg font-semibold mb-4">Retention (Latest Cohorts)</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.retention.slice(0, 5)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="cohort_date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis width={50} domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                  <Tooltip
                    formatter={(value) => [`${value}%`, 'Retention']}
                    labelFormatter={(date) => new Date(date).toLocaleDateString('en-US')}
                  />
                  <Legend />
                  <Bar dataKey="day_1_retention" name="1-Day Retention" fill={COLORS[0]} />
                  <Bar dataKey="day_7_retention" name="7-Day Retention" fill={COLORS[1]} />
                  <Bar dataKey="day_30_retention" name="30-Day Retention" fill={COLORS[2]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-lg font-semibold mb-4">Top 5 Challenges</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.challenges.slice(0, 5)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" />
                  <YAxis
                    dataKey="task"
                    type="category"
                    width={150}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(task) => `${task.substring(0, 12)}...`}
                  />
                  <Tooltip content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const challenge = data.challenges.find(c => c.task === payload[0].payload.task);
                      return (
                        <div className="bg-white p-3 border rounded shadow text-sm">
                          <p className="text-base font-medium mb-1">{challenge.icon} {challenge.task}</p>
                          <p>Completions: <b>{challenge.completion_count}</b></p>
                          <p>Unique Users: <b>{challenge.unique_users}</b></p>
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Legend />
                  <Bar dataKey="completion_count" name="Number of Completions" fill={COLORS[3]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-lg font-semibold mb-4">Streak Distribution</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.streaks}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    dataKey="user_count"
                    nameKey="streak_length"
                  >
                    {data.streaks.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [
                    `${value} users`,
                    `${props.payload.streak_length} day streak`
                  ]} />
                  <Legend
                    formatter={(value) => `${value} day streak`}
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {activeTab === 'challenges' && (
          <div className="challenge-section bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
            <h3 className="text-xl font-semibold mb-6">Challenge Analysis</h3>
            <table className="challenge-table min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th style={{ width: '40%' }} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Challenge</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unique Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Hour</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.challenges.map((challenge, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <span className="challenge-emoji">{challenge.icon || challenge.emoji}</span>
                      {challenge.task || challenge.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{challenge.completion_count || challenge.completions}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{challenge.unique_users || challenge.uniqueUsers}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {challenge.avg_completion_hour ? `${challenge.avg_completion_hour}:00` : challenge.avgHour}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'retention' && retentionStats && (
          <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
            <h2 className="text-xl font-semibold mb-6">Retention Analysis</h2>
            {/* Add retention visualizations here similar to second version */}
          </div>
        )}

        {activeTab === 'streaks' && streakStats && (
          <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
            <h2 className="text-xl font-semibold mb-6">Streak Analysis</h2>
            {/* Add streak visualizations here similar to second version */}
          </div>
        )}

        {activeTab === 'monetization' && (
          <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
            <h2 className="text-xl font-semibold mb-6">Monetization Analysis</h2>
            {/* Add monetization visualizations here similar to second version */}
          </div>
        )}

        {activeTab === 'sharing' && (
          <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
            <h2 className="text-xl font-semibold mb-6">Sharing Analysis</h2>
            {/* Add sharing visualizations here similar to second version */}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;